import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'
import { ToggleButton, type ToggleButtonProps } from './toggle-button'

export type SegmentedControlOrientation = 'horizontal' | 'vertical' | 'responsive'

const controlOrientationClassNames: Record<SegmentedControlOrientation, string> = {
  // Inline flex still stretches inside flex/grid parents; a horizontal group fits its options.
  horizontal: 'inline-flex w-fit rounded-full',
  vertical: 'flex flex-col rounded-xl',
  responsive:
    'flex flex-col rounded-xl @md/segmented-control:flex-row @md/segmented-control:rounded-full',
}

const itemOrientationClassNames: Record<SegmentedControlOrientation, string> = {
  horizontal: 'first:rounded-l-full last:rounded-r-full',
  vertical: 'first:rounded-t-xl last:rounded-b-xl',
  responsive:
    'first:rounded-t-xl last:rounded-b-xl @md/segmented-control:first:rounded-l-full @md/segmented-control:first:rounded-r-none @md/segmented-control:last:rounded-l-none @md/segmented-control:last:rounded-r-full',
}

export type SegmentedControlProps = ComponentProps<'div'> & {
  orientation?: SegmentedControlOrientation
}

function SegmentedControl({
  className,
  orientation = 'horizontal',
  ...props
}: SegmentedControlProps) {
  return (
    <div
      {...props}
      data-slot="segmented-control"
      data-orientation={orientation}
      className={cn(
        'isolate min-w-0 max-w-full gap-px border border-control-border bg-control-border',
        controlOrientationClassNames[orientation],
        className
      )}
    />
  )
}

export type SegmentedControlItemProps = Omit<ToggleButtonProps, 'pressed' | 'pressedVariant'> & {
  selected?: boolean
  selectedVariant?: ToggleButtonProps['pressedVariant']
  orientation?: SegmentedControlOrientation
}

function SegmentedControlItem({
  className,
  selected = false,
  variant = 'outline',
  selectedVariant = 'selection',
  orientation = 'horizontal',
  role,
  'aria-checked': ariaChecked,
  ...props
}: SegmentedControlItemProps) {
  return (
    <ToggleButton
      {...props}
      role={role}
      pressed={selected}
      variant={variant}
      pressedVariant={selectedVariant}
      aria-checked={role === 'radio' ? (ariaChecked ?? selected) : ariaChecked}
      data-slot="segmented-control-item"
      data-orientation={orientation}
      className={cn(
        'relative min-w-0 rounded-none border-0! shadow-none transition-none enabled:active:scale-100 focus-visible:z-10 data-[state=on]:ring-1 data-[state=on]:ring-inset data-[state=on]:ring-selection-border',
        itemOrientationClassNames[orientation],
        className
      )}
    />
  )
}

export { SegmentedControl, SegmentedControlItem }
